import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { WithServices } from 'v5/platform/ts/services';
import { WithFeatureManagementService } from 'v5/services/front/FeatureManagementService/FeatureManagementService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { FeatureVersionSwitch } from 'v5/patterns/widgets/version-switch/FeatureVersionSwitch';

/** Beta pill in the resource pane header: v6 editor ↔ the classic pane. */
export function ResourceEditorVersionSwitch({
  services
}: WithServices<
  [
    WithFeatureService,
    WithFeatureManagementService,
    WithAppScopeService,
    WithToastService,
    WithSupportService
  ]
>) {
  const { t } = useTranslation('resources');
  return (
    <FeatureVersionSwitch
      services={services}
      featureName="resource-pane"
      newVersionName="v6"
      rollbackVersionName="new"
      title={t('ResourceEditorVersionSwitch.title')}
      newVersionDescription={t(
        'ResourceEditorVersionSwitch.newVersionDescription'
      )}
      oldVersionDescription={t(
        'ResourceEditorVersionSwitch.oldVersionDescription'
      )}
      newVersionPreview={
        <>
          <span className="h-2 rounded-sm bg-bg-subtle-2" />
          <SketchValue labelWidth="w-1/3" valueWidth="w-3/5" />
          <SketchValue labelWidth="w-1/4" valueWidth="w-1/2" />
          <span className="flex gap-1 px-1">
            <span className="h-1.5 w-1/5 rounded-sm bg-blue-200" />
            <span className="h-1.5 w-1/5 rounded-sm bg-bg-subtle-2" />
          </span>
        </>
      }
      oldVersionPreview={
        <>
          <SketchInput labelWidth="w-1/3" />
          <SketchInput labelWidth="w-1/4" />
          <SketchInput labelWidth="w-1/5" />
        </>
      }
    />
  );
}

function SketchValue({
  labelWidth,
  valueWidth
}: {
  labelWidth: string;
  valueWidth: string;
}) {
  return (
    <span className="flex flex-col gap-0.5 px-1">
      <span className={cn('h-1 rounded-sm bg-fg-subtle/30', labelWidth)} />
      <span className={cn('h-1 rounded-sm bg-fg-default/70', valueWidth)} />
    </span>
  );
}

function SketchInput({ labelWidth }: { labelWidth: string }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className={cn('h-1 rounded-sm bg-fg-subtle/40', labelWidth)} />
      <span className="h-2.5 rounded-sm border border-border-default" />
    </span>
  );
}
